import { Box } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import { SelectedChip } from './MultipleDropdownStyle';

interface SelectedChipItem {
  key: string;
  label: string;
}

interface SelectedChipsRowProps {
  items: SelectedChipItem[];
}

const CHIP_GAP_PX = 4;
const OVERFLOW_CHIP_WIDTH_PX = 44;

function SelectedChipsRow({ items }: SelectedChipsRowProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [visibleCount, setVisibleCount] = useState(items.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    /**
     * Recalculate how many chips fit in the container's current width
     */
    const recalculateVisibleCount = () => {
      const containerWidth = container.clientWidth;
      let usedWidth = 0;
      let count = 0;

      for (let index = 0; index < items.length; index += 1) {
        const chipWidth = chipRefs.current[index]?.offsetWidth ?? 0;
        const isLastItem = index === items.length - 1;
        const reserveForOverflowChip = isLastItem ? 0 : OVERFLOW_CHIP_WIDTH_PX + CHIP_GAP_PX;

        if (usedWidth + chipWidth + reserveForOverflowChip > containerWidth) {
          break;
        }
        usedWidth += chipWidth + CHIP_GAP_PX;
        count += 1;
      }

      setVisibleCount(Math.max(count, 1));
    };

    recalculateVisibleCount();

    const resizeObserver = new ResizeObserver(recalculateVisibleCount);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [items]);

  const hiddenCount = items.length - visibleCount;

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Shadow row: every chip rendered off-screen purely to measure its natural width */}
      <Box sx={{ position: 'absolute', visibility: 'hidden', display: 'flex', gap: `${CHIP_GAP_PX}px` }}>
        {items.map((item, index) => (
          <SelectedChip
            key={item.key}
            label={item.label}
            size="small"
            ref={(el: HTMLDivElement | null) => {
              chipRefs.current[index] = el;
            }}
          />
        ))}
      </Box>

      {/* Visible row: only the chips that fit, plus a "+N" chip for the rest */}
      <Box ref={containerRef} sx={{ display: 'flex', gap: `${CHIP_GAP_PX}px`, overflow: 'hidden', flexWrap: 'nowrap' }}>
        {items.slice(0, visibleCount).map((item) => (
          <SelectedChip key={item.key} label={item.label} size="small" />
        ))}
        {hiddenCount > 0 ? <SelectedChip label={`+${hiddenCount}`} size="small" /> : null}
      </Box>
    </Box>
  );
}

export default SelectedChipsRow;
